import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Component from '../../components/Component';
import {
  createGameCatalogItem,
  deleteGameCatalogItem,
  getGameCatalogPayload,
  isGameCatalogApiSuccess,
  listGameCatalog,
  patchGameCatalogItem,
} from '../../services/gameCatalogApi';
import {
  buildPayload,
  emptyForm,
  formatDateTime,
  formatVersionWindow,
  isRowVisibleForAppVersion,
  KNOWN_FEATURE_KEYS,
  normalizeItems,
  rowToForm,
  SURFACE_OPTIONS,
  validateForm,
} from './gameCatalogUtils';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

function GameCatalog() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [surfaceFilter, setSurfaceFilter] = useState('ALL');
  const [enabledFilter, setEnabledFilter] = useState('ALL');
  const [previewVersion, setPreviewVersion] = useState('');
  const [helpExpanded, setHelpExpanded] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [formErrors, setFormErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingKey, setTogglingKey] = useState(null);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleSessionExpired = useCallback(
    (res) => {
      if (String(res?.statusCode || '') === '10003') {
        showSnackbar('Session expired. Please login again.', 'error');
        setTimeout(() => {
          window.location = '/login';
        }, 1500);
        return true;
      }
      return false;
    },
    [showSnackbar]
  );

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await listGameCatalog();
      if (handleSessionExpired(res)) return;
      if (!isGameCatalogApiSuccess(res)) {
        setError(res?.message || 'Failed to load game catalog.');
        setItems([]);
        return;
      }
      setItems(normalizeItems(getGameCatalogPayload(res)));
    } catch (err) {
      setError(err?.message || 'Failed to load game catalog.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [handleSessionExpired]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const filteredItems = useMemo(() => {
    return items.filter((row) => {
      if (surfaceFilter !== 'ALL' && row.surface !== surfaceFilter) return false;
      if (enabledFilter === 'ENABLED' && !row.is_enabled) return false;
      if (enabledFilter === 'DISABLED' && row.is_enabled) return false;
      return true;
    });
  }, [items, surfaceFilter, enabledFilter]);

  const previewActive = Boolean(String(previewVersion || '').trim());

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const openCreate = () => {
    setEditingKey(null);
    setForm(emptyForm());
    setFormErrors({});
    setFormError('');
    setDrawerOpen(true);
  };

  const openEdit = (row) => {
    setEditingKey(row.game_key);
    setForm(rowToForm(row));
    setFormErrors({});
    setFormError('');
    setDrawerOpen(true);
  };

  const handleSave = async () => {
    const isCreate = !editingKey;
    const errors = validateForm(form, { isCreate });
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      setFormError('Fix the highlighted fields.');
      return;
    }

    setSaving(true);
    setFormError('');
    try {
      const body = buildPayload(form, { isCreate });
      const res = isCreate
        ? await createGameCatalogItem(body)
        : await patchGameCatalogItem(editingKey, body);

      if (handleSessionExpired(res)) return;
      if (!isGameCatalogApiSuccess(res)) {
        setFormError(res?.message || 'Save failed.');
        return;
      }

      showSnackbar(isCreate ? 'Catalog row created.' : 'Catalog row updated.');
      setDrawerOpen(false);
      fetchList();
    } catch {
      setFormError('Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleQuickToggle = async (row) => {
    const nextEnabled = !row.is_enabled;
    setTogglingKey(row.game_key);
    try {
      const res = await patchGameCatalogItem(row.game_key, { is_enabled: nextEnabled });
      if (handleSessionExpired(res)) return;
      if (!isGameCatalogApiSuccess(res)) {
        showSnackbar(res?.message || 'Failed to update.', 'error');
        return;
      }
      const updated = getGameCatalogPayload(res);
      setItems((prev) =>
        prev.map((item) =>
          item.game_key === row.game_key
            ? { ...item, ...(updated || {}), is_enabled: nextEnabled }
            : item
        )
      );
      showSnackbar(nextEnabled ? 'Enabled.' : 'Disabled for everyone.');
    } catch {
      showSnackbar('Failed to update.', 'error');
    } finally {
      setTogglingKey(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await deleteGameCatalogItem(deleteTarget.game_key);
      if (handleSessionExpired(res)) return;
      if (!isGameCatalogApiSuccess(res)) {
        showSnackbar(res?.message || 'Delete failed.', 'error');
        return;
      }
      showSnackbar('Catalog row deleted.');
      setDeleteTarget(null);
      fetchList();
    } catch {
      showSnackbar('Delete failed.', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const isCreate = !editingKey;
  const isHubTile = form.surface === 'hub_tile';

  return (
    <Component>
      <div className="page-body">
        <div className="container-fluid">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, mt: 1 }}>
            <Button component={Link} to="/admin/arcade" startIcon={<ArrowBackIcon />} size="small">
              Arcade
            </Button>
            <Typography variant="h5" sx={{ flex: 1 }}>
              Game catalog
            </Typography>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchList} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              Add game
            </Button>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Controls which hub tiles and feature gates appear per app version. Visible when enabled and{' '}
            <code>min ≤ app_version &lt; max</code> (null bounds open). Header <code>app_version</code> only —
            never <code>users.app_version</code>.
          </Typography>

          <Button
            size="small"
            startIcon={<InfoOutlinedIcon />}
            onClick={() => setHelpExpanded((v) => !v)}
            sx={{ mb: 2 }}
          >
            {helpExpanded ? 'Hide formula' : 'Version formula & seeds'}
          </Button>

          <Collapse in={helpExpanded}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Missing / unparseable <code>app_version</code> = old client: hide if min is set, else show
                (still respects max). Ship the native game first, then set <code>min_app_version</code> — no
                force update.
              </Typography>
              <Typography variant="body2" component="div">
                Seed floors (do not lower unless old binaries can render):{' '}
                {Object.entries(KNOWN_FEATURE_KEYS).map(([key, meta]) => (
                  <Chip
                    key={key}
                    size="small"
                    sx={{ mr: 0.5, mb: 0.5 }}
                    label={`${key} ≥ ${meta.seedMin}`}
                  />
                ))}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Delete restores known feature keys to code seed floors. Zero hub_tile rows → static four-tile
                fallback.
              </Typography>
            </Alert>
          </Collapse>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              <TextField
                select
                label="Surface"
                size="small"
                value={surfaceFilter}
                onChange={(e) => setSurfaceFilter(e.target.value)}
                sx={{ minWidth: 140 }}
              >
                <MenuItem value="ALL">All</MenuItem>
                <MenuItem value="hub_tile">Hub tile</MenuItem>
                <MenuItem value="feature">Feature</MenuItem>
              </TextField>
              <TextField
                select
                label="Enabled"
                size="small"
                value={enabledFilter}
                onChange={(e) => setEnabledFilter(e.target.value)}
                sx={{ minWidth: 140 }}
              >
                <MenuItem value="ALL">All</MenuItem>
                <MenuItem value="ENABLED">Enabled only</MenuItem>
                <MenuItem value="DISABLED">Disabled only</MenuItem>
              </TextField>
              <TextField
                label="Preview app_version"
                size="small"
                placeholder="e.g. 2.3.5"
                value={previewVersion}
                onChange={(e) => setPreviewVersion(e.target.value)}
                helperText="Client-side: which rows would be visible"
                sx={{ minWidth: 180 }}
              />
              <Chip size="small" label={`${filteredItems.length} shown`} />
              <Chip size="small" label={`${items.length} total`} variant="outlined" />
            </Box>
          </Paper>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>game_key</TableCell>
                    <TableCell>Surface</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Version window</TableCell>
                    <TableCell align="right">Sort</TableCell>
                    <TableCell>Nav / image</TableCell>
                    {previewActive && <TableCell>Preview</TableCell>}
                    <TableCell>Enabled</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={previewActive ? 9 : 8} align="center" sx={{ py: 4 }}>
                        No catalog rows match filters.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredItems.map((row) => {
                      const visible = previewActive
                        ? isRowVisibleForAppVersion(row, previewVersion)
                        : null;
                      const featureMeta = KNOWN_FEATURE_KEYS[row.game_key];
                      return (
                        <TableRow
                          key={row.game_key}
                          hover
                          sx={
                            previewActive && !visible
                              ? { opacity: 0.45 }
                              : undefined
                          }
                        >
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace">
                              {row.game_key}
                            </Typography>
                            {featureMeta && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {featureMeta.label}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={row.surface === 'hub_tile' ? 'hub_tile' : 'feature'}
                              color={row.surface === 'hub_tile' ? 'primary' : 'secondary'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{row.title || '—'}</TableCell>
                          <TableCell>
                            <Typography variant="body2">{formatVersionWindow(row)}</Typography>
                            {(row.min_app_version || row.max_app_version) && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                min {row.min_app_version || '∅'} · max {row.max_app_version || '∅'} (excl.)
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">{row.sort_order ?? 0}</TableCell>
                          <TableCell>
                            {row.surface === 'hub_tile' ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, maxWidth: 220 }}>
                                {row.image_url ? (
                                  <Box
                                    component="img"
                                    src={row.image_url}
                                    alt=""
                                    sx={{
                                      width: 36,
                                      height: 36,
                                      objectFit: 'cover',
                                      borderRadius: 1,
                                      bgcolor: 'action.hover',
                                    }}
                                    onError={(e) => {
                                      e.currentTarget.style.visibility = 'hidden';
                                    }}
                                  />
                                ) : null}
                                <Typography
                                  variant="caption"
                                  fontFamily="monospace"
                                  sx={{ wordBreak: 'break-all' }}
                                >
                                  {row.navigation_link || '—'}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                —
                              </Typography>
                            )}
                          </TableCell>
                          {previewActive && (
                            <TableCell>
                              <Chip
                                size="small"
                                label={visible ? 'Visible' : 'Hidden'}
                                color={visible ? 'success' : 'default'}
                              />
                            </TableCell>
                          )}
                          <TableCell>
                            <Switch
                              size="small"
                              checked={!!row.is_enabled}
                              disabled={togglingKey === row.game_key}
                              onChange={() => handleQuickToggle(row)}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => openEdit(row)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton size="small" color="error" onClick={() => setDeleteTarget(row)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </div>
      </div>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => !saving && setDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 440 } } }}
      >
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
          <Typography variant="h6">{isCreate ? 'Add catalog row' : `Edit ${editingKey}`}</Typography>

          {formError && (
            <Alert severity="error" onClose={() => setFormError('')}>
              {formError}
            </Alert>
          )}

          {isCreate ? (
            <TextField
              label="game_key"
              value={form.game_key}
              onChange={(e) => setField('game_key', e.target.value.toLowerCase())}
              required
              fullWidth
              error={!!formErrors.game_key}
              helperText={formErrors.game_key || 'lowercase [a-z][a-z0-9_]{0,63}'}
              disabled={saving}
            />
          ) : (
            <TextField label="game_key" value={editingKey} fullWidth disabled />
          )}

          <TextField
            select
            label="surface"
            value={form.surface}
            onChange={(e) => setField('surface', e.target.value)}
            required
            fullWidth
            error={!!formErrors.surface}
            helperText={formErrors.surface || 'hub_tile → arcade tiles; feature → gates'}
            disabled={saving || !isCreate}
          >
            {SURFACE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="title"
            value={form.title}
            onChange={(e) => setField('title', e.target.value)}
            required
            fullWidth
            error={!!formErrors.title}
            helperText={formErrors.title}
            disabled={saving}
          />

          {isHubTile && (
            <>
              <TextField
                label="image_url"
                value={form.image_url}
                onChange={(e) => setField('image_url', e.target.value)}
                required
                fullWidth
                error={!!formErrors.image_url}
                helperText={formErrors.image_url}
                disabled={saving}
              />
              <TextField
                label="navigation_link"
                value={form.navigation_link}
                onChange={(e) => setField('navigation_link', e.target.value)}
                required
                fullWidth
                error={!!formErrors.navigation_link}
                helperText={formErrors.navigation_link || 'FE route, e.g. /arcade/browse/spin-wheel'}
                disabled={saving}
              />
            </>
          )}

          {!isHubTile && (
            <>
              <TextField
                label="image_url (optional)"
                value={form.image_url}
                onChange={(e) => setField('image_url', e.target.value)}
                fullWidth
                disabled={saving}
              />
              <TextField
                label="navigation_link (optional)"
                value={form.navigation_link}
                onChange={(e) => setField('navigation_link', e.target.value)}
                fullWidth
                disabled={saving}
              />
            </>
          )}

          <TextField
            label="sort_order"
            type="number"
            value={form.sort_order}
            onChange={(e) => setField('sort_order', e.target.value)}
            fullWidth
            error={!!formErrors.sort_order}
            helperText={formErrors.sort_order || 'Lower first within surface'}
            disabled={saving}
          />

          <TextField
            label="min_app_version"
            value={form.min_app_version}
            onChange={(e) => setField('min_app_version', e.target.value)}
            fullWidth
            error={!!formErrors.min_app_version}
            helperText={
              formErrors.min_app_version ||
              'Inclusive floor. Clear (empty) = no min. 2.4 → stored as 2.4.0'
            }
            disabled={saving}
            placeholder="e.g. 2.4.0"
          />

          <TextField
            label="max_app_version"
            value={form.max_app_version}
            onChange={(e) => setField('max_app_version', e.target.value)}
            fullWidth
            error={!!formErrors.max_app_version}
            helperText={
              formErrors.max_app_version ||
              'Exclusive ceiling. Clear (empty) = no max. Must be above min when both set.'
            }
            disabled={saving}
            placeholder="e.g. 2.5.0"
          />

          <FormControlLabel
            control={
              <Switch
                checked={!!form.is_enabled}
                onChange={(e) => setField('is_enabled', e.target.checked)}
                disabled={saving}
              />
            }
            label="is_enabled"
          />

          {editingKey && (
            <Typography variant="caption" color="text.secondary">
              Updated {formatDateTime(items.find((i) => i.game_key === editingKey)?.updated_at)}
            </Typography>
          )}

          <Box sx={{ mt: 'auto', display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button onClick={() => setDrawerOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? <CircularProgress size={20} /> : isCreate ? 'Create' : 'Save'}
            </Button>
          </Box>
        </Box>
      </Drawer>

      <Dialog open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)}>
        <DialogTitle>Delete catalog row?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Remove <strong>{deleteTarget?.game_key}</strong>
            {deleteTarget?.title ? ` (${deleteTarget.title})` : ''}?
          </Typography>
          {deleteTarget && KNOWN_FEATURE_KEYS[deleteTarget.game_key] && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Known feature key — after delete, public APIs fall back to seed floor{' '}
              <code>≥ {KNOWN_FEATURE_KEYS[deleteTarget.game_key].seedMin}</code>.
            </Alert>
          )}
          {deleteTarget?.surface === 'hub_tile' && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              If this was the last hub_tile row, clients fall back to the static four-tile list.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Component>
  );
}

export default GameCatalog;
